import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TrailPanelData {
  trail: {
    id: string;
    name: string;
    difficulties: string[];
    length_miles: number;
    surface_types: string[];
    forests: string[];
    districts: string[];
  } | null;
  stats: {
    routeCount: number;
    runCount: number;
    lastRunDate: string | null;
    recentRunPhoto: string | null;
    totalPhotos: number;
  };
  isLoading: boolean;
  error: string | null;
}

export const useTrailPanelData = (trailId?: string) => {
  const [data, setData] = useState<TrailPanelData>({
    trail: null,
    stats: {
      routeCount: 0,
      runCount: 0,
      lastRunDate: null,
      recentRunPhoto: null,
      totalPhotos: 0,
    },
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (!trailId) {
      setData({
        trail: null,
        stats: {
          routeCount: 0,
          runCount: 0,
          lastRunDate: null,
          recentRunPhoto: null,
          totalPhotos: 0,
        },
        isLoading: false,
        error: null,
      });
      return;
    }

    const fetchPanelData = async () => {
      setData(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        // Fetch trail aggregate data
        const { data: trailData, error: trailError } = await supabase
          .from('trails')
          .select('id, name, length_miles')
          .eq('id', trailId)
          .single();

        if (trailError) throw trailError;

        // Fetch all segments to aggregate distinct metadata
        const { data: segmentsData } = await supabase
          .from('trail_segments')
          .select('id, difficulty, surface_type, forest_name, district_name')
          .eq('trail_id', trailId);

        // Aggregate distinct values
        const difficulties = segmentsData 
          ? [...new Set(segmentsData.map(s => s.difficulty).filter(Boolean))] as string[]
          : [];
        const surface_types = segmentsData
          ? [...new Set(segmentsData.map(s => s.surface_type).filter(Boolean))] as string[]
          : [];
        const forests = segmentsData
          ? [...new Set(segmentsData.map(s => s.forest_name).filter(Boolean))] as string[]
          : [];
        const districts = segmentsData
          ? [...new Set(segmentsData.map(s => s.district_name).filter(Boolean))] as string[]
          : [];

        // Get all segment IDs for this trail
        const segmentIds = segmentsData ? segmentsData.map(s => s.id) : [];

        // Fetch route count using segment IDs
        const { count: routeCount } = await supabase
          .from('route_segments')
          .select('route_id', { count: 'exact', head: true })
          .in('trail_segment_id', segmentIds);

        // Fetch run stats and recent photo using segment IDs
        const { data: runSegments } = await supabase
          .from('run_segments')
          .select('run_id, runs!inner(end_time, photo_urls, visibility)')
          .in('trail_segment_id', segmentIds)
          .in('runs.visibility', ['public', 'anonymous'])
          .order('runs(end_time)', { ascending: false });

        const uniqueRunIds = new Set();
        let mostRecentPhoto: string | null = null;
        let totalPhotos = 0;
        let lastRunDate: string | null = null;

        if (runSegments && runSegments.length > 0) {
          runSegments.forEach((rs: any) => {
            uniqueRunIds.add(rs.run_id);
            const run = rs.runs;
            
            if (run.end_time && !lastRunDate) {
              lastRunDate = run.end_time;
            }

            if (run.photo_urls && Array.isArray(run.photo_urls) && run.photo_urls.length > 0) {
              totalPhotos += run.photo_urls.length;
              if (!mostRecentPhoto) {
                mostRecentPhoto = run.photo_urls[0];
              }
            }
          });
        }

        setData({
          trail: trailData ? {
            id: trailData.id,
            name: trailData.name,
            difficulties,
            length_miles: trailData.length_miles || 0,
            surface_types,
            forests,
            districts,
          } : null,
          stats: {
            routeCount: routeCount || 0,
            runCount: uniqueRunIds.size,
            lastRunDate,
            recentRunPhoto: mostRecentPhoto,
            totalPhotos,
          },
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Error fetching trail panel data:', error);
        setData(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load trail data',
        }));
      }
    };

    fetchPanelData();
  }, [trailId]);

  return data;
};
